import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import { useQueryDocumentosContrato } from '@/shared/actions/erp/tickets-soporte-cliente';
import type { IDocumentoItem, IVision360Contrato } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { toApiError } from '@/shared/utils/general/axios.utils';
import DocumentoCategoriaCard from './DocumentoCategoriaCard';
import VisorDocumentoDialog from './VisorDocumentoDialog';
import { CATEGORIAS_DOCUMENTOS, CategoriaDocumentoKey, esImagen } from './documentos.constants';

interface TabDocumentosProps {
  contrato: IVision360Contrato;
  can: (perm: string) => boolean;
}

// El panel lo monta con key={id_contrato}: al cambiar de contrato la
// categoria activa vuelve a "Contratos".
export default function TabDocumentos({ contrato, can }: TabDocumentosProps) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQueryDocumentosContrato(contrato.id_contrato);
  const puedeDescargar = can('vision360.descargar_documentos');
  const [categoriaActiva, setCategoriaActiva] = useState<CategoriaDocumentoKey>('contratos');
  const [documentoAbierto, setDocumentoAbierto] = useState<IDocumentoItem | null>(null);
  
  const documentos = useMemo(() => data?.data, [data])
  

  const lista = documentos?.[categoriaActiva] ?? [];
  const apiError = isError ? toApiError(error) : null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <DescriptionOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>Documentos</Typography>
        <Chip size="small" variant="outlined" label={contrato.codigo} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Actualizar">
          <span>
            <IconButton size="small" aria-label="Actualizar documentos" onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {isLoading && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {CATEGORIAS_DOCUMENTOS.map((c) => (
              <Grid item xs={12} sm={6} lg={3} key={c.key}>
                <Skeleton variant="rounded" height={104} />
              </Grid>
            ))}
          </Grid>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />
          ))}
        </>
      )}

      {apiError && (
        apiError.status === 403 ? (
          <Alert severity="error">No tienes permiso para ver documentos (vision360.ver_documentos).</Alert>
        ) : (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => refetch()}>Reintentar</Button>}
          >
            {apiError.message}
          </Alert>
        )
      )}

      {documentos && (
        <>
          {!documentos.softnet_disponible && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No se pudo consultar Softnet; contratos, adendas y parte de actualización débito pueden estar incompletos.
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {CATEGORIAS_DOCUMENTOS.map((c) => (
              <Grid item xs={12} sm={6} lg={3} key={c.key}>
                <DocumentoCategoriaCard
                  categoria={c}
                  total={documentos[c.key].length}
                  activa={categoriaActiva === c.key}
                  onClick={() => setCategoriaActiva(c.key)}
                />
              </Grid>
            ))}
          </Grid>

          {lista.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 5, color: 'text.secondary' }}>
              <FolderOffOutlinedIcon sx={{ fontSize: 48 }} />
              <Typography variant="body2">Sin documentos en esta categoría</Typography>
            </Stack>
          ) : (
            <>
              {/* Escritorio: tabla */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Origen</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lista.map((doc) => (
                      <TableRow key={doc.url} hover>
                        <TableCell sx={{ maxWidth: 0, width: '60%' }}>
                          <NombreDocumento documento={doc} />
                        </TableCell>
                        <TableCell>
                          <OrigenChip documento={doc} />
                        </TableCell>
                        <TableCell align="right">
                          <AccionesDocumento documento={doc} puedeDescargar={puedeDescargar} onVer={setDocumentoAbierto} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Movil: tarjetas apiladas */}
              <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {lista.map((doc) => (
                  <Card key={doc.url} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <NombreDocumento documento={doc} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                      <OrigenChip documento={doc} />
                      <AccionesDocumento documento={doc} puedeDescargar={puedeDescargar} onVer={setDocumentoAbierto} />
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </>
      )}

      <VisorDocumentoDialog documento={documentoAbierto} puedeDescargar={puedeDescargar} onClose={() => setDocumentoAbierto(null)} />
    </Paper>
  );
}

function NombreDocumento({ documento }: { documento: IDocumentoItem }) {
  const Icono = esImagen(documento.url) ? ImageOutlinedIcon : PictureAsPdfOutlinedIcon;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Icono fontSize="small" color="error" />
      <Typography variant="body2" fontWeight={500} noWrap title={documento.nombre}>
        {documento.nombre}
      </Typography>
    </Stack>
  );
}

function OrigenChip({ documento }: { documento: IDocumentoItem }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={documento.origen}
      color={documento.origen === 'SOFTNET' ? 'info' : 'warning'}
    />
  );
}

interface AccionesDocumentoProps {
  documento: IDocumentoItem;
  puedeDescargar: boolean;
  onVer: (doc: IDocumentoItem) => void;
}

function AccionesDocumento({ documento, puedeDescargar, onVer }: AccionesDocumentoProps) {
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Ver">
        <IconButton size="small" aria-label={`Ver ${documento.nombre}`} onClick={() => onVer(documento)}>
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {puedeDescargar && (
        <Tooltip title="Descargar">
          {/* Dominio externo: el atributo download se ignora, se abre en otra pestaña. */}
          <IconButton
            size="small"
            aria-label={`Descargar ${documento.nombre}`}
            component="a"
            href={documento.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
